import { automationsKeys } from "@punchup/data/hooks";
import type { CreditMode, CreditTier } from "@punchup/data/models";
import { dayjs, getApiClient } from "@punchup/utils";
import { useEffect, useRef } from "react";

import type { CreditTransaction } from "@/data/v2/models/messaging-credits.types";
import { logException } from "@punchup/sentry";
import { useQuery } from "@tanstack/react-query";

export interface MessagingCreditsBalance {
  freeBalance: number;
  purchasedBalance: number;
  totalBalance: number;
}

export async function fetchCreditBalance(
  artistId: string
): Promise<MessagingCreditsBalance> {
  try {
    const result = await getApiClient().get<{
      data?: {
        free_balance: number;
        purchased_balance: number;
        total_balance: number;
      };
    }>(`/api/v2/manager/credits/balance?artistId=${artistId}`);

    return {
      freeBalance: result.data?.free_balance ?? 0,
      purchasedBalance: result.data?.purchased_balance ?? 0,
      totalBalance: result.data?.total_balance ?? 0,
    };
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId },
      tags: { source: "fetchCreditBalance" },
    });
    throw error;
  }
}

export async function fetchCreditCheckoutSession(
  artistId: string,
  tier: CreditTier,
  mode: CreditMode,
  comedianName?: string
): Promise<{ clientSecret: string }> {
  try {
    const result = await getApiClient().post<{
      data?: { clientSecret: string };
    }>("/api/v2/manager/credits/checkout-session", {
      artistId,
      tier,
      mode,
      comedianName,
    });

    return { clientSecret: result.data?.clientSecret ?? "" };
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId, tier, mode },
      tags: { source: "fetchCreditCheckoutSession" },
    });
    throw error;
  }
}

export async function fetchCreditTransactions(
  artistId: string
): Promise<CreditTransaction[]> {
  try {
    const result = await getApiClient().get<{ data?: CreditTransaction[] }>(
      `/api/v2/manager/credits/transactions?artistId=${artistId}`
    );
    return result.data ?? [];
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId },
      tags: { source: "fetchCreditTransactions" },
    });
    throw error;
  }
}

export function useMessagingCredits(artistId: string) {
  return useQuery({
    queryKey: automationsKeys.creditBalance(artistId),
    queryFn: () => fetchCreditBalance(artistId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!artistId,
  });
}

/** Shape of an active credit subscription returned by the API */
export interface ActiveSubscription {
  subscriptionId: string;
  tier: CreditTier;
  creditAmount: number;
  priceFormatted: string;
  updatedGeneration: boolean;
  renewalDate: string | null;
  status: "active" | "past_due";
}

export async function fetchActiveSubscription(
  artistId: string
): Promise<ActiveSubscription | null> {
  try {
    const result = await getApiClient().get<{
      data?: { subscription: ActiveSubscription | null };
    }>(`/api/v2/manager/credits/subscription?artistId=${artistId}`);
    return result.data?.subscription ?? null;
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId },
      tags: { source: "fetchActiveSubscription" },
    });
    throw error;
  }
}

export async function cancelCreditSubscription(
  artistId: string,
  subscriptionId: string
): Promise<void> {
  try {
    await getApiClient().post<{ data?: { canceled: boolean } }>(
      "/api/v2/manager/credits/subscription",
      { action: "cancel", artistId, subscriptionId }
    );
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId, subscriptionId },
      tags: { source: "cancelCreditSubscription" },
    });
    throw error;
  }
}

export async function changeCreditSubscriptionTier(
  artistId: string,
  subscriptionId: string,
  newTier: CreditTier
): Promise<void> {
  try {
    await getApiClient().post<{ data?: { updated: boolean; tier: string } }>(
      "/api/v2/manager/credits/subscription",
      { action: "change_tier", artistId, subscriptionId, newTier }
    );
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId, subscriptionId, newTier },
      tags: { source: "changeCreditSubscriptionTier" },
    });
    throw error;
  }
}

export async function previewCreditTierChange(
  artistId: string,
  subscriptionId: string,
  newTier: CreditTier
): Promise<number> {
  try {
    const result = await getApiClient().post<{
      data?: { amountDueCents: number };
    }>("/api/v2/manager/credits/subscription", {
      action: "preview_change_tier",
      artistId,
      subscriptionId,
      newTier,
    });
    // A missing figure must throw (→ range-copy fallback), never read as $0.00.
    if (result.data?.amountDueCents == null) {
      throw new Error("Malformed tier-change preview response");
    }
    return result.data.amountDueCents;
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId, subscriptionId, newTier },
      tags: { source: "previewCreditTierChange" },
    });
    throw error;
  }
}

export async function fetchPortalSessionUrl(artistId: string): Promise<string> {
  try {
    const result = await getApiClient().post<{ data?: { url: string } }>(
      "/api/v2/manager/credits/portal-session",
      { artistId }
    );
    return result.data?.url ?? "";
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      extra: { artistId },
      tags: { source: "fetchPortalSessionUrl" },
    });
    throw error;
  }
}

export function useActiveSubscription(artistId: string) {
  return useQuery({
    queryKey: ["creditSubscription", artistId],
    queryFn: () => fetchActiveSubscription(artistId),
    staleTime: 2 * 60 * 1000,
    enabled: !!artistId,
  });
}

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 30_000;

export function usePostPurchaseBalancePoller(
  artistId: string,
  opts: {
    enabled: boolean;
    previousBalance: number;
    onBalanceUpdated: () => void;
  }
) {
  const { enabled, previousBalance, onBalanceUpdated } = opts;
  const startTimeRef = useRef<number>(dayjs().valueOf());
  const calledCallbackRef = useRef<boolean>(false);

  // Reset start time and callback flag when enabled transitions to true
  useEffect(() => {
    if (enabled) {
      startTimeRef.current = dayjs().valueOf();
      calledCallbackRef.current = false;
    }
  }, [enabled]);

  return useQuery({
    queryKey: automationsKeys.creditBalance(artistId),
    queryFn: () => fetchCreditBalance(artistId),
    staleTime: 0,
    enabled: !!artistId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data as MessagingCreditsBalance | undefined;
      const elapsed = dayjs().diff(startTimeRef.current, "ms");

      if (elapsed >= POLL_TIMEOUT_MS) {
        return false;
      }

      if (data && data.totalBalance > previousBalance) {
        if (!calledCallbackRef.current) {
          calledCallbackRef.current = true;
          // Call in next tick to avoid calling during render
          setTimeout(onBalanceUpdated, 0);
        }
        return false;
      }

      return POLL_INTERVAL_MS;
    },
  });
}
